import Emitter from 'es6-event-emitter';
import msgpack from 'msgpack-lite';
import qs from 'qs';
import 'whatwg-fetch';
import di from "dependency-injection";

/**
 * サーバー同期（ajax）
 *
 * @package     editech-hub
 * @author      Kenichi Kashiwagi <pod@cro-co.co.jp>
 */
class ApiBase extends Emitter {
    /**
     * コンストラクタ
     *
     * @access public
     * @return void
     */
    constructor() {
        super();

        this.MSGPACK_TYPE = "application/x-msgpack";
        this.JSON_TYPE = "application/json";

        const e = document.querySelector("meta[name=csrf-token]");
        this.csrfToken = e ? e.getAttribute("content") : '';
    }

    /**
     * GET
     *
     * @access public
     * @return this
     */
    get(url, data = null) {
        if (null != data) {
            url += qs.stringify(data, true);
        }

        return fetch(url, {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }).then(function(response) {
            const contentType = response.headers.get('Content-Type');
            if (contentType.startsWith(this.MSGPACK_TYPE)) {
                return this.decode(response.body);
            } else {
                return response.json();
            }
        }.bind(this)).catch(function(error) {
            console.log(error)
        }.bind(this));
    }

    apiJson(url, data = {}) {
        return this.apiPost(url, data, { jsonmode: true });
    }

    /**
     * ApiPost
     * 
     * @param {string} url 
     * @param {(FormData|Object|Array)} data
     * @param {boolean} jsonmode 
     * @param {boolean} loading 
     * @returns 
     */
    apiPost (url, data = {}, { jsonmode = false, loading = true } = {}) {
        const request = {
            method: 'POST',
            mode: 'cors',
            cache: 'no-cache',
            credentials: 'same-origin',
        };
        if (jsonmode) {
            request.body = JSON.stringify({...data, ['csrf-token']: this.csrfToken});
            request.headers = {
                'Content-Type': 'application/json',
                "X-Croco-ApiPost": "json"
            }
        } else if (data instanceof FormData) {
            request.body = data;
            request.body.set('csrf-token', this.csrfToken);
            request.headers = {
                "X-Croco-ApiPost": "json"
            }
        } else if (data instanceof Object || data instanceof Array) {
            request.body = qs.stringify({...data, ['csrf-token']: this.csrfToken})
            request.headers = {
                'Content-Type': 'application/x-www-form-urlencoded',
                "X-Croco-ApiPost": "json"
            }
        }

        const loader = di.loader && loading ? di.loader.setState : () => {};
        loader(true);
        return fetch(url, request).then(res => {
            if (!res.ok) {
                throw new Error('invalid response: ' + res.status);
            }
            return res.json();
        }).then(json => {
            if (json.signedOut) {
                location.href = "/";
            }
            if (json.denied) {
                di.SyncBox.trigger(`permission:Error`, json.message);
                return;
            }
            if (!json.success) {
                if (json.data) {
                    throw json;
                }
                if (json.message == 'invalid csrf-token.') {
                    di.toast.push("トークンが無効です。リロードしてください。", "negative");
                    throw new Error(json.message);
                }
                throw new Error(json.message);
            }
            return json.data;
        }).finally(() => {
            loader(false);
        });
    }

    /**
     * POST
     *
     * @access public
     * @return this
     */
    post (url, data = {}) {
        return fetch(url, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: qs.stringify({...data, ['csrf-token']: this.csrfToken})
        }).then((response) => {
            const contentType = response.headers.get('Content-Type');
            if (contentType.startsWith(this.MSGPACK_TYPE)) {
                return this.decode(response.body, response.headers.get('Content-Length'));
            } else {
                return response.json();
            }
        }).catch((error) => {
            console.log(error)
        });
    }

    /**
     * stream data decoder
     *
     * @access public
     * @return this
     */
    decode(stream, length) {
        let size = length ? length : 4096;
        let reader = stream.getReader();
        let buff = new Uint8Array(size);

        return reader.read().then(function readChunk({ done, value }) {
            if (done) {
                return msgpack.decode(buff);
            }
            buff.set(value);

            return reader.read().then(readChunk);
        });
    }
}

export default ApiBase;